import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Send to error reporting service if enabled
    if (typeof window !== 'undefined' && window.reportError) {
      try {
        window.reportError(error, {
          errorInfo,
          errorId: this.state.errorId,
          component: 'ErrorBoundary',
          timestamp: new Date().toISOString()
        });
      } catch (reportingError) {
        console.warn('Error reporting failed:', reportingError);
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            
            {/* Error Message */}
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Something went wrong
            </h1>
            
            <p className="text-slate-600 mb-6">
              We're sorry, but something unexpected happened. Please try reloading the page.
            </p>
            
            {/* Error ID for support */}
            {this.state.errorId && (
              <div className="bg-slate-100 rounded-lg p-3 mb-6">
                <p className="text-xs text-slate-500 mb-1">Error ID (for support):</p>
                <code className="text-xs font-mono text-slate-700 select-all break-all">
                  {this.state.errorId}
                </code>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={this.handleReload}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
              
              <Button
                onClick={this.handleReset}
                variant="outline"
                className="w-full text-slate-600 border-slate-200 hover:bg-slate-50"
              >
                Try Again
              </Button>
            </div>
            
            {/* Development Error Details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-700">
                  Show Error Details (Development)
                </summary>
                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <pre className="text-xs text-red-800 whitespace-pre-wrap overflow-auto max-h-40">
                    {this.state.error && this.state.error.toString()}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}
            
            {/* Contact Support */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                If this problem persists, please contact support with the error ID above.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;